/* eslint-disable camelcase  */
import { useAuth0 } from '@auth0/auth0-react';
import {
  Button,
  Card,
  Checkbox,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputAdornment,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TablePagination,
  TextField,
  Typography,
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import useAxiosWithAuth from '../api/useAxiosWithAuth';
import Iconify from '../components/Iconify';
import ModernAgGrid from '../components/ModernAgGrid';
import Page from '../components/Page';
import Scrollbar from '../components/Scrollbar';
import useImportLocationsQuery from '../hooks/useImportLocationsQuery';

const ManageExportsSupplier = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isLoading, setIsLoading] = useState(true);
  const [filterValue, setFilterValue] = useState('');
  const [tableData, setTableData] = useState([]);
  const [columnDefs, setColumnDefs] = useState([]);
  const [selectedImportLocation, setSelectedImportLocation] = useState('');
  const [selectedPermitStatuses, setSelectedPermitStatuses] = useState(['Applied For', 'Unused', 'Sent to Exporter']);
  const [searchValue, setSearchValue] = useState('');
  const [sortColumnName, setSortColumnName] = useState('');
  const [sortType, setSortType] = useState(''); // 'asc', 'desc', or ''
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [exportModalOpen, setExportModalOpen] = useState(false);

  const axiosInstance = useAxiosWithAuth();
  const gridRef = useRef();
  const { user } = useAuth0();
  const { locations: importLocations, loading: importLocationsLoading } = useImportLocationsQuery();

  // Function to handle column header clicks for sorting
  const handleSort = (columnName) => {
    if (sortColumnName === columnName) {
      // Same column is clicked again
      if (sortType === 'asc') {
        // First click: asc -> desc
        setSortType('desc');
      } else if (sortType === 'desc') {
        // Second click: desc -> none (reset)
        setSortColumnName('');
        setSortType('');
      }
    } else {
      // Different column is clicked - start fresh with asc
      setSortColumnName(columnName);
      setSortType('asc');
    }
    // Reset to first page when sorting changes
    setPage(0);
  };

  // Helper function to get sort indicator
  const getSortIndicator = (field) => {
    if (sortColumnName === field) {
      if (sortType === 'asc') {
        return '↓'; // Down arrow for ascending
      }
      if (sortType === 'desc') {
        return '↑'; // Up arrow for descending
      }
    }
    return '↕'; // Default icon for unsorted columns
  };

  const formatDateForDisplay = (date) => {
    if (!date) return '';

    // Check if the date string is the default/minimal date from API
    if (date === '0001-01-01T00:00:00Z') {
      return 'N/A';
    }

    let dateObj;

    if (typeof date === 'string') {
      // Check if it's an RFC3339 formatted string or the custom format with milliseconds
      if (date.includes('T') && (date.includes('Z') || date.includes('+'))) {
        dateObj = new Date(date);
      } else {
        dateObj = new Date(date);
      }
    } else if (typeof date === 'number') {
      // If it's a Unix timestamp
      dateObj = new Date(date * 1000);
    } else {
      // If it's already a Date object
      dateObj = date;
    }

    // Format as DD/MM/YYYY
    const day = String(dateObj.getDate()).padStart(2, '0');
    const month = String(dateObj.getMonth() + 1).padStart(2, '0'); // Month is 0-indexed
    const year = dateObj.getFullYear();

    return `${day}/${month}/${year}`;
  };

  const [total, setTotal] = useState(0);

  // Set up column definitions for AG-Grid
  useEffect(() => {
    setColumnDefs([
      {
        field: 'sl',
        headerName: '#SL',
        resizable: true,
        flex: 0.5,
        minWidth: 80,
        headerComponent: (params) => (
          <div
            style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => handleSort('sl')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleSort('sl');
              }
            }}
            role="button"
            tabIndex={0}
          >
            <span style={{ flex: 1 }}>#SL</span>
            <span style={{ marginLeft: '4px' }}>{getSortIndicator('sl')}</span>
          </div>
        ),
      },
      {
        field: 'permit_status',
        headerName: 'Status',
        resizable: true,
        flex: 1,
        minWidth: 120,
        headerComponent: (params) => (
          <div
            style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => handleSort('permit_status')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleSort('permit_status');
              }
            }}
            role="button"
            tabIndex={0}
          >
            <span style={{ flex: 1 }}>Status</span>
            <span style={{ marginLeft: '4px' }}>{getSortIndicator('permit_status')}</span>
          </div>
        ),
      },
      {
        field: 'product',
        headerName: 'Product',
        resizable: true,
        flex: 1.5,
        minWidth: 150,
        headerComponent: (params) => (
          <div
            style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => handleSort('product')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleSort('product');
              }
            }}
            role="button"
            tabIndex={0}
          >
            <span style={{ flex: 1 }}>Product</span>
            <span style={{ marginLeft: '4px' }}>{getSortIndicator('product')}</span>
          </div>
        ),
      },
      {
        field: 'qty_applied',
        headerName: 'Qty Applied',
        resizable: true,
        flex: 0.8,
        minWidth: 100,
        headerComponent: (params) => (
          <div
            style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => handleSort('qty_applied')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleSort('qty_applied');
              }
            }}
            role="button"
            tabIndex={0}
          >
            <span style={{ flex: 1 }}>Qty Applied</span>
            <span style={{ marginLeft: '4px' }}>{getSortIndicator('qty_applied')}</span>
          </div>
        ),
      },
      {
        field: 'import_location',
        headerName: 'Import Location',
        resizable: true,
        flex: 1.2,
        minWidth: 150,
        headerComponent: (params) => (
          <div
            style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => handleSort('import_location')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleSort('import_location');
              }
            }}
            role="button"
            tabIndex={0}
          >
            <span style={{ flex: 1 }}>Import Location</span>
            <span style={{ marginLeft: '4px' }}>{getSortIndicator('import_location')}</span>
          </div>
        ),
      },
      {
        field: 'application_submitted',
        headerName: 'Application Submitted',
        resizable: true,
        flex: 1.2,
        minWidth: 150,
        valueFormatter: (params) => formatDateForDisplay(params.value),
        headerComponent: (params) => (
          <div
            style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => handleSort('application_submitted')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleSort('application_submitted');
              }
            }}
            role="button"
            tabIndex={0}
          >
            <span style={{ flex: 1 }}>Application Submitted</span>
            <span style={{ marginLeft: '4px' }}>{getSortIndicator('application_submitted')}</span>
          </div>
        ),
      },
      {
        field: 'import_permit',
        headerName: 'Date Permit Received',
        resizable: true,
        flex: 1.2,
        minWidth: 150,
        valueFormatter: (params) => formatDateForDisplay(params.value),
        headerComponent: (params) => (
          <div
            style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => handleSort('import_permit')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleSort('import_permit');
              }
            }}
            role="button"
            tabIndex={0}
          >
            <span style={{ flex: 1 }}>Date Permit Received</span>
            <span style={{ marginLeft: '4px' }}>{getSortIndicator('import_permit')}</span>
          </div>
        ),
      },
      {
        field: 'original_permit_no',
        headerName: 'Original Permit No.',
        resizable: true,
        flex: 1.2,
        minWidth: 150,
        cellRendererFramework: (params) =>
          params.data.file_name ? (
            <a href={params.data.file_name} target="_blank" rel="noopener noreferrer">
              {params.data.original_permit_no}
            </a>
          ) : (
            <span>{params.data.original_permit_no === 'undefined' ? '' : params.data.original_permit_no}</span>
          ),
        headerComponent: (params) => (
          <div
            style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => handleSort('original_permit_no')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleSort('original_permit_no');
              }
            }}
            role="button"
            tabIndex={0}
          >
            <span style={{ flex: 1 }}>Original Permit No.</span>
            <span style={{ marginLeft: '4px' }}>{getSortIndicator('original_permit_no')}</span>
          </div>
        ),
      },
      {
        field: 'permit_expiry',
        headerName: 'Permit Expiry',
        resizable: true,
        flex: 1,
        minWidth: 120,
        valueFormatter: (params) => formatDateForDisplay(params.value),
        headerComponent: (params) => (
          <div
            style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => handleSort('permit_expiry')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleSort('permit_expiry');
              }
            }}
            role="button"
            tabIndex={0}
          >
            <span style={{ flex: 1 }}>Permit Expiry</span>
            <span style={{ marginLeft: '4px' }}>{getSortIndicator('permit_expiry')}</span>
          </div>
        ),
      },
    ]);
  }, [tableData, sortColumnName, sortType]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    const getData = async () => {
      try {
        setIsLoading(true);
        // Calculate the statuses to ignore based on selected statuses
        const allStatuses = ['Applied For', 'Used', 'Unused', 'Sent to Exporter'];
        const statusesToIgnore = allStatuses.filter(status => !selectedPermitStatuses.includes(status));
        // If all statuses are selected, send [""], otherwise send the filtered array
        const finalIgnoreArray = statusesToIgnore.length === 0 ? [""] : statusesToIgnore;

        const response = await axiosInstance.post(
          `/trade/supplier-exports/${user['http://healthhouse.com/supplier_code']
          }?page_size=${rowsPerPage}&page_number=${page + 1}`,
          {
            search_key: filterValue,
            import_location: selectedImportLocation, // Use the selected import location
            sort_column_name: sortColumnName, // Use the selected sort column
            sort_type: sortType, // Use the selected sort type
            ignore_permit_status: finalIgnoreArray,
          }
        );

        if (response.status === 200) {
          // The new response structure has meta and payload
          const { payload: data, meta } = response.data;
          setTotal(meta.total_size || data.length); // Set total for pagination from meta

          // Add serial numbers to the data
          const dataWithSerial = data.map((item, index) => ({
            ...item,
            sl: index + 1 + page * rowsPerPage, // Calculate serial based on current page
          }));

          setTableData(dataWithSerial);
        }
      } catch (error) {
        console.error('Error fetching exports:', error);
        setTableData([]);
        if (error.response?.data?.message) {
          toast.error(`Failed to fetch exports: ${error.response.data.message}`);
        } else {
          toast.error('Failed to fetch exports. Please try again.');
        }
      } finally {
        setIsLoading(false);
      }
    };

    getData();
  }, [rowsPerPage, page, filterValue, selectedImportLocation, sortColumnName, sortType, selectedPermitStatuses]);

  useEffect(() => {
    return setPage(0);
  }, []);

  // Set default dates when component mounts
  useEffect(() => {
    const today = new Date();
    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(today.getMonth() - 6);

    setStartDate(sixMonthsAgo);
    setEndDate(today);
  }, []);

  // Export function to download CSV
  const handleExport = async () => {
    try {
      // Format dates to YYYY-MM-DD format
      const formatDate = (date) => {
        if (!date) return '';
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      const formattedStartDate = startDate ? formatDate(startDate) : '';
      const formattedEndDate = endDate ? formatDate(endDate) : '';

      // Calculate the statuses to ignore based on selected statuses
      const allStatuses = ['Applied For', 'Used', 'Unused', 'Sent to Exporter'];
      const statusesToIgnore = allStatuses.filter(status => !selectedPermitStatuses.includes(status));
      // If all statuses are selected, send [""], otherwise send the filtered array
      const finalIgnoreArray = statusesToIgnore.length === 0 ? [""] : statusesToIgnore;

      // Call the API with the same filters plus date filters
      const response = await axiosInstance.post(
        `/trade/supplier-exports/${user['http://healthhouse.com/supplier_code']}?page_size=15000&page_number=1`, // High limit to get all records
        {
          search_key: filterValue,
          import_location: selectedImportLocation,
          sort_column_name: sortColumnName,
          sort_type: sortType,
          start_date: formattedStartDate, // Add start date filter
          end_date: formattedEndDate, // Add end date filter
          ignore_permit_status: finalIgnoreArray,
        }
      );

      if (response.status === 200) {
        const { payload: data } = response.data;
        // Add serial numbers to the data
        const dataWithSerial = data.map((item, index) => ({
          ...item,
          sl: index + 1,
        }));
        downloadCSV(dataWithSerial);
        // Close the modal after successful export
        setExportModalOpen(false);
      }
    } catch (error) {
      console.error('Error exporting data:', error);
      toast.error('Failed to export data. Please try again.');
    }
  };

  // Function to convert data to CSV and download
  const downloadCSV = (data) => {
    if (!data || data.length === 0) {
      toast.warning('No data to export');
      return;
    }

    // Define the CSV headers based on the data structure
    const headers = [
      'SL',
      'Status',
      'Product',
      'Qty Applied',
      'Import Location',
      'Application Submitted',
      'Date Permit Received',
      'Original Permit No.',
      'Permit Expiry',
    ];

    // Format the data rows
    const rows = data.map((item) => [
      item.sl || '',
      item.permit_status || '',
      item.product || '',
      item.qty_applied || '',
      item.import_location || '',
      formatDateForDisplay(item.application_submitted) || '',
      formatDateForDisplay(item.import_permit) || '',
      item.original_permit_no || '',
      formatDateForDisplay(item.permit_expiry) || '',
    ]);

    // Create CSV content
    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.map((field) => `"${String(field).replace(/"/g, '""')}"`).join(',')),
    ].join('\n');

    // Create a Blob with the CSV content
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

    // Create a link element to trigger the download
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    // Set the filename with current date
    const date = new Date().toISOString().slice(0, 10);
    link.setAttribute('href', url);
    link.setAttribute('download', `supplier_exports_${date}.csv`);
    link.style.visibility = 'hidden';

    // Append, click, and remove the link to trigger download
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Page title="Manage Exports">
      <Container maxWidth="xl">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2} ml={2}>
          <Typography variant="h4" gutterBottom>
            Manage Exports
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            {(searchValue || selectedImportLocation) && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  setSearchValue('');
                  setFilterValue('');
                  setSelectedImportLocation('');
                }}
              >
                Clear Filters
              </Button>
            )}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                setFilterValue(searchValue);
                setPage(0); // Reset to first page when searching
              }}
            >
              <OutlinedInput
                size="small"
                placeholder="Search..."
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                endAdornment={
                  <InputAdornment position="end">
                    <button type="submit" style={{ border: 'none', background: 'none', cursor: 'pointer' }}>
                      <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled', width: 20, height: 20 }} />
                    </button>
                  </InputAdornment>
                }
                sx={{ minWidth: 200 }}
              />
            </form>
            <FormControl sx={{ minWidth: 200 }} size="small">
              <InputLabel id="import-location-label">Import Location</InputLabel>
              <Select
                labelId="import-location-label"
                id="importLocation"
                value={selectedImportLocation}
                label="Import Location"
                onChange={(e) => {
                  setSelectedImportLocation(e.target.value);
                  setPage(0); // Reset to first page when changing filter
                }}
              >
                {importLocationsLoading ? (
                  <MenuItem value="" disabled>
                    <CircularProgress size={20} />
                    <Typography variant="body2" sx={{ ml: 1 }}>
                      Loading...
                    </Typography>
                  </MenuItem>
                ) : (
                  importLocations.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>

            <FormControl sx={{ minWidth: 250 }} size="small">
              <InputLabel id="permit-statuses-label">Permit Statuses</InputLabel>
              <Select
                labelId="permit-statuses-label"
                id="permitStatuses"
                multiple
                value={selectedPermitStatuses}
                onChange={(e) => {
                  const value = e.target.value;
                  // If all options are selected, use the array as is
                  // If it's a function (in case of special selections), handle appropriately
                  const newValue = typeof value === 'function' ? value(selectedPermitStatuses) : value;
                  setSelectedPermitStatuses(newValue);
                  setPage(0); // Reset to first page when changing filter

                  // Refresh the export list to get the updated data from the server
                  const refreshData = async () => {
                    try {
                      setIsLoading(true);
                      // Calculate the statuses to ignore based on selected statuses
                      const allStatuses = ['Applied For', 'Used', 'Unused', 'Sent to Exporter'];
                      const statusesToIgnore = allStatuses.filter(status => !newValue.includes(status));
                      // If all statuses are selected, send [""], otherwise send the filtered array
                      const finalIgnoreArray = statusesToIgnore.length === 0 ? [""] : statusesToIgnore;

                      const response = await axiosInstance.post(
                        `/trade/supplier-exports/${user['http://healthhouse.com/supplier_code']
                        }?page_size=${rowsPerPage}&page_number=1`, // Always fetch page 1 when filter changes
                        {
                          search_key: filterValue,
                          import_location: selectedImportLocation, // Use the selected import location
                          sort_column_name: sortColumnName, // Use the selected sort column
                          sort_type: sortType, // Use the selected sort type
                          ignore_permit_status: finalIgnoreArray,
                        }
                      );

                      if (response.status === 200) {
                        // The new response structure has meta and payload
                        const { payload: data, meta } = response.data;
                        setTotal(meta.total_size || data.length); // Set total for pagination from meta

                        // Add serial numbers to the data
                        const dataWithSerial = data.map((item, index) => ({
                          ...item,
                          sl: index + 1, // Calculate serial starting from 1 for page 1
                        }));

                        setTableData(dataWithSerial);
                      }
                      setIsLoading(false);
                    } catch (error) {
                      setIsLoading(false);
                      console.error('Error refreshing exports:', error);
                    }
                  };

                  refreshData();
                }}
                renderValue={(selected) => selected.join(', ')}
                label="Permit Statuses"
              >
                {['Applied For', 'Used', 'Unused', 'Sent to Exporter'].map((status) => (
                  <MenuItem key={status} value={status}>
                    <Checkbox checked={selectedPermitStatuses.indexOf(status) > -1} />
                    <ListItemText primary={status} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="contained"
              startIcon={<Iconify icon="eva:download-outline" />}
              onClick={() => setExportModalOpen(true)}
              sx={{ ml: 2 }}
            >
              Export
            </Button>
          </Stack>
        </Stack>

        <Card sx={{ m: 2 }}>
          <Scrollbar>
            <div style={{ height: 520, width: '100%', p: 3 }}>
              {isLoading ? (
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexDirection: 'column',
                  }}
                >
                  <CircularProgress size={72} sx={{ mb: 3 }} />
                  <h5>Please wait while we prepare your data</h5>
                </div>
              ) : (
                <ModernAgGrid
                  rowData={tableData}
                  columnDefs={columnDefs}
                  gridRef={gridRef}
                  getRowId={(params) => params.data.sl}
                  style={{ height: 520, width: '100%' }}
                />
              )}
            </div>
          </Scrollbar>

          <TablePagination
            rowsPerPageOptions={[10, 15, 25, 50, 100]}
            component="div"
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>
        {/* Export Modal */}
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Dialog open={exportModalOpen} onClose={() => setExportModalOpen(false)}>
            <DialogTitle>Export Data</DialogTitle>
            <DialogContent>
              <DialogContentText sx={{ mb: 2 }}>Select date range for export:</DialogContentText>
              <Stack spacing={2} sx={{ minWidth: 300, mt: 1 }}>
                <DatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={(newValue) => {
                    setStartDate(newValue);
                  }}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
                <DatePicker
                  label="End Date"
                  value={endDate}
                  onChange={(newValue) => {
                    setEndDate(newValue);
                  }}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setExportModalOpen(false)}>Cancel</Button>
              <Button variant="contained" onClick={handleExport} startIcon={<Iconify icon="eva:download-outline" />}>
                Export
              </Button>
            </DialogActions>
          </Dialog>
        </LocalizationProvider>
      </Container>
    </Page>
  );
};

export default ManageExportsSupplier;
